import { Fragment, h, render } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import { EffectFade } from "swiper/modules";

import {
  FaqIcon,
  WarrantyIcon,
  ArrowLeft,
  ArrowRight,
} from "../helpers/icons.jsx";
import ZoomableImage from "./zoomable-image.jsx";
import ReactModal from "../helpers/react-modal.jsx";

import storefront from "../helpers/storefront";
import * as queries from "../helpers/storefront/queries";
import FadeImage from "./fade-image.jsx";
import { isColor } from "../helpers/colors.js";

const ZoomMessage = () => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setShow(false);
    }, 2000);
  }, []);

  return (
    <div
      style={{ opacity: show ? 1 : 0 }}
      className="h5 product-media__zoom-message"
    >
      Click to Zoom
    </div>
  );
};

const areDifferentColor = (variant1, variant2) => {
  try {
    const findColor = (options) =>
      options.find((option) => isColor(option.name));
    const color1 = findColor(variant1.selectedOptions);
    const color2 = findColor(variant2.selectedOptions);
    return color1.value !== color2.value;
  } catch (e) {
    return false;
  }
};

const isHostedVideo = (item) =>
  item?.mediaContentType?.toLowerCase() === "video" ||
  item?.media_type?.toLowerCase() === "video";

const isYouTubeVideo = (item) =>
  (item?.host || "").toLowerCase() === "youtube" ||
  /youtube|youtu\.be/.test(item?.embeddedUrl || "");

const getExternalId = (item) =>
  item?.external_id || item?.embeddedUrl?.split("/").pop().split("?")[0];

// Shopify video sources hold the original upload (mp4 or mov), lower-res
// mp4 renditions, and an m3u8 HLS playlist. HLS doesn't play natively
// outside Safari, and sources are empty until Shopify finishes transcoding.
const getVideoSrc = (item) => {
  const sources = item?.sources || [];
  const mp4s = sources
    .filter((s) => s.format?.toLowerCase() === "mp4")
    .sort((a, b) => (b.height || 0) - (a.height || 0));
  const capped = mp4s.find((s) => (s.height || 0) <= 1080);
  const mov = sources.find((s) => s.format?.toLowerCase() === "mov");
  return (capped || mp4s[0] || mov)?.url;
};

const getVideoPoster = (item) => {
  const poster = item?.preview_image?.src || item?.previewImage?.originalSrc;
  return poster ? getSizedImageUrl(poster, "1200x") : null;
};

const isRenderableMedia = (item) =>
  !isHostedVideo(item) || Boolean(getVideoSrc(item) || getVideoPoster(item));

const HostedVideo = ({ item, alt }) => {
  const src = getVideoSrc(item);

  if (!src) {
    const poster = getVideoPoster(item);
    return poster ? (
      <img
        src={poster}
        alt={alt}
        className="product-media__image product-media__image--contain"
      />
    ) : null;
  }

  return (
    <div className="video video--product">
      <video
        src={src}
        poster={getVideoPoster(item)}
        autoplay
        muted
        loop
        playsinline
        preload="metadata"
        aria-label={alt}
      ></video>
    </div>
  );
};

const MediaContent = ({ data }) => {
  const swiper = useRef(null);
  const modalSwiper = useRef(null);
  const [product, setProduct] = useState(data.product);
  const [currentVariant, setCurrentVariant] = useState(
    data.originalProduct.variants.find(
      (variant) =>
        `gid://shopify/ProductVariant/${variant.id}` === data.initialVariantId,
    ),
  );

  const [productHandle, setProductHandle] = useState(null);
  const [oos, setOos] = useState(null);
  const [media, setMedia] = useState(product.media);
  const [galleryFade, setGalleryFade] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialModalSlide, setInitialModalSlide] = useState(0);
  const [firstImageOpacity, setFirstImageOpacity] = useState(1);
  const [placeholderRemoved, setPlaceholderRemoved] = useState(false);

  const removePlaceholderImage = () => {
    if (placeholderRemoved) return;

    setPlaceholderRemoved(true);

    const placeholders = document.querySelectorAll(
      ".product-media__container--placeholder, .product-main__media-placeholder",
    );

    placeholders.forEach((placeholder) => placeholder.remove());
  };

  let screenMobile = window.matchMedia("(max-width: 992px)");

  const handleMediaClick = (slideIndex) => {
    setInitialModalSlide(slideIndex);
    setModalOpen(true);
  };

  const variantImages = product.variants.map(
    (edge) => edge.featured_image?.src,
  );
  const [variantImage, setVariantImage] = useState(
    currentVariant?.featured_image?.src,
  );

  const images = media
    .filter((i) => {
      return !variantImages.find((v) => {
        return v === i.src;
      });
    })
    .filter(isRenderableMedia);

  const [filteredMedia, setFilteredMedia] = useState(images);

  const swiperModalParams = {
    slidesPerView: "auto",
    spaceBetween: 0,
    direction: "horizontal",
    effect: "fade",
    noSwipingClass: "swiper-slide",
    initialSlide: initialModalSlide,
    eventsPrefix: "pdp-modal-swiper",
    thumbs: {
      swiper: "[data-product-media-modal-thumbs]",
    },
  };

  const swiperModalThumbParams = {
    direction: "horizontal",
    spaceBetween: 20,
    slidesPerView: "auto",
    watchSlidesProgress: true,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
    initialSlide: initialModalSlide,
    eventsPrefix: "pdp-modal-thumbs-swiper",
    breakpoints: {
      768: {
        direction: "vertical",
      },
    },
    grid: {
      rows: 1,
    },
  };
  const swiperParams = {
    thumbs: {
      swiper: "[data-product-media-thumbs]",
    },
    pagination: {
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 3,
      enabled: true,
    },
    slidesPerView: 1,
    speed: 300,
    loop: true,
    rebuildOnUpdate: true,
    eventsPrefix: "pdp-gallery-swiper",
    breakpoints: {
      992: {
        pagination: {
          enabled: false,
        },
      },
    },
  };

  const thumbsSwiperParams = {
    spaceBetween: 16,
    slidesPerView: "auto",
    watchSlidesProgress: true,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
    eventsPrefix: "pdp-thumbs-swiper",
  };

  useEffect(() => {
    const handleProductUpdate = (e) => {
      let v = e?.detail?.variant,
        p = e.detail.product;
      setCurrentVariant(v);
      v?.featured_image?.url && setVariantImage(v.featured_image.url);
      v?.image?.url && setVariantImage(v.image.url);
      p?.handle && setProductHandle(p?.handle);
      window.setVariantImage = setVariantImage;
      swiper?.current?.swiper.slideTo(0, 300);
      modalSwiper?.current?.swiper.slideTo(0, 300);
    };
    document.addEventListener("productUpdate", handleProductUpdate, false);

    return () => {
      document.removeEventListener("productUpdate", handleProductUpdate, false);
    };
  }, [
    swiper,
    setVariantImage,
    setProductHandle,
    setCurrentVariant,
    currentVariant,
  ]);

  useEffect(async () => {
    if (!product) return;
    const response = await storefront.request({
      query: queries.product,
      variables: {
        handle: product.handle,
      },
    });

    let p = response.data?.productByHandle,
      variants = p?.variants?.edges,
      variant = variants
        ? variants.find(({ node }) => node.id === data.initialVariantId).node
        : null;

    if (!variant) return;
    setCurrentVariant(variant);
  }, [product]);

  const getAltText = (item) => {
    let fullBleed =
      item?.alt?.includes("#full-bleed") ||
      item?.altText?.includes("#full-bleed");
    return fullBleed ? product?.title : item.alt;
  };

  const getImagePadding = (item) => {
    let fullBleed =
      item?.alt?.includes("#full-bleed") ||
      item?.altText?.includes("#full-bleed");
    return fullBleed ? "full" : "";
  };

  const getImageFit = (item) => {
    let fullBleed =
      item?.alt?.includes("#full-bleed") ||
      item?.altText?.includes("#full-bleed");
    return fullBleed ? "cover" : "contain";
  };

  useEffect(async () => {
    if (!productHandle) return;
    setGalleryFade(true);
    const response = await storefront.request({
      query: queries.product,
      variables: {
        handle: productHandle,
      },
    });

    if (!response?.data?.productByHandle) return;
    let sp = response.data.productByHandle,
      si = sp?.media.edges,
      v = sp?.variants.edges,
      vim = v && v[0]?.image?.src;

    let updatedImages = si.filter((i) => {
      if (i.node.mediaContentType != "IMAGE") return true;
      return !v.find((vi) => {
        return (
          vi.node.src === i.node.image?.src ||
          vi?.node?.image?.src === i.node.image?.src
        );
      });
    });
    updatedImages = updatedImages.map((i) => i.node);
    updatedImages = updatedImages.map((i) => (i.image ? i.image : i));
    updatedImages = updatedImages.filter(isRenderableMedia);
    setFilteredMedia(updatedImages);
    setTimeout(() => {
      setGalleryFade(false);
    }, 200);
  }, [productHandle]);

  const handleThumbClick = (index) =>
    modalSwiper?.current?.swiper.slideTo(index);

  useEffect(() => {
    let available = currentVariant.availableForSale || currentVariant.available,
      quantity =
        (currentVariant.inventoryQuantity &&
          currentVariant.inventoryQuantity > 0) ||
        (currentVariant.quantityAvailable &&
          currentVariant.quantityAvailable > 0);

    let message = currentVariant?.oosMessage?.value
      ? currentVariant?.oosMessage?.value
      : product.oosMessage;

    !available || (!quantity && setOos(message));
  }, [currentVariant]);

  const slideTo = (instance, direction) => {
    if (!instance) return;
    const offset = direction === "next" ? 1 : -1;
    const slideCount = instance.slides.length;

    let newIndex = instance.realIndex + offset;

    if (newIndex >= slideCount) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = slideCount - 1;
    }

    instance.slideToLoop(newIndex, 300);
  };

  return (
    <Fragment>
      {data.warranty && (
        <div className="product-media__warranty copy-text">
          <WarrantyIcon />
          Best in class warranty
        </div>
      )}
      {data.faqs && (
        <a className="product-media__faqs-badge caption" href="#productFaqs">
          <FaqIcon />
          FAQ
        </a>
      )}
      <swiper-container
        {...swiperParams}
        ref={swiper}
        className="product-media__container"
        data-product-media-gallery
      >
        <div slot="container-end">
          <div className="product-media__navigation-wrapper">
            <button
              className="product-media__navigation"
              onClick={() => slideTo(swiper?.current.swiper, "prev")}
            >
              <span className="visually-hidden">Previous image</span>
              <ArrowLeft />
            </button>
            <button
              className="product-media__navigation"
              onClick={() => slideTo(swiper?.current.swiper, "next")}
            >
              <span className="visually-hidden">Next image</span>
              <ArrowRight />
            </button>
          </div>
        </div>
        {currentVariant && variantImage && (
          <swiper-slide
            data-product-image
            data-slide={0}
            onClick={() => handleMediaClick(0)}
            className="swiper-slide"
          >
            <FadeImage
              className={`product-media__image product-media__image--${getImageFit(
                currentVariant.featured_image || currentVariant.image,
              )} ${galleryFade ? "fade" : ""}`}
              src={getSizedImageUrl(variantImage, "1200x")}
              alt={getAltText(
                currentVariant.featured_image || currentVariant.image,
              )}
              fetchPriority="high"
              onLoad={() => removePlaceholderImage()}
            />
          </swiper-slide>
        )}
        {filteredMedia.map((item, index) => (
          <swiper-slide
            data-product-image
            data-slide={variantImage ? index + 1 : index}
            onClick={() => handleMediaClick(variantImage ? index + 1 : index)}
            key={item?.src}
            className="swiper-slide"
          >
            {item.mediaContentType?.toLowerCase() === "image" ? (
              <img
                src={getSizedImageUrl(item?.src, "1200x")}
                height={600 * (item ? item?.aspect_ratio : 1)}
                width={600}
                alt={getAltText(item)}
                className={`product-media__image product-media__image--${getImageFit(item)} ${
                  galleryFade ? "fade" : ""
                }`}
                loading={index === 0 && !variantImage ? false : "false"}
                onLoad={() => {
                  index === 0 && setFirstImageOpacity(1);
                  removePlaceholderImage();
                }}
                style={{ opacity: firstImageOpacity }}
              />
            ) : item.mediaContentType?.toLowerCase() === "external_video" ||
              item.media_type?.toLowerCase() === "external_video" ? (
              <div className="video video--product">
                <iframe
                  frameborder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen="allowfullscreen"
                  src={
                    isYouTubeVideo(item)
                      ? `https://www.youtube.com/embed/${getExternalId(
                          item,
                        )}?autoplay=1&mute=1&controls=0&loop=1&playlist=${getExternalId(
                          item,
                        )}&playsinline=1&rel=0`
                      : `https://player.vimeo.com/video/${getExternalId(
                          item,
                        )}?background=1&controls=0`
                  }
                  title={getAltText(item)}
                ></iframe>
              </div>
            ) : isHostedVideo(item) ? (
              <HostedVideo item={item} alt={getAltText(item)} />
            ) : (
              <img
                src={getSizedImageUrl(item?.src, "1200x")}
                height={600 * (item ? item?.aspect_ratio : 1)}
                width={600}
                alt={getAltText(item)}
                className={`product-media__image product-media__image--${getImageFit(item)} ${
                  galleryFade ? "fade" : ""
                }`}
                loading={index === 0 && !variantImage ? false : "false"}
                onLoad={() => {
                  index === 0 && setFirstImageOpacity(1);
                  removePlaceholderImage();
                }}
                style={{ opacity: firstImageOpacity }}
              />
            )}
          </swiper-slide>
        ))}
      </swiper-container>
      <swiper-container
        {...thumbsSwiperParams}
        className="product-media__thumbs"
        data-product-media-thumbs
      >
        {currentVariant && variantImage && (
          <swiper-slide className="swiper-slide">
            <button type="button" className="product-media__thumb-button">
              <img
                src={getSizedImageUrl(variantImage, "80x")}
                height="80"
                width="80"
                loading="lazy"
                className={`image product-media__thumb-image`}
                alt={getAltText(
                  currentVariant.featured_image || currentVariant.image,
                )}
              />
            </button>
          </swiper-slide>
        )}
        {filteredMedia.map((item, index) => (
          <swiper-slide
            className="swiper-slide"
            onClick={() => handleThumbClick(variantImage ? index + 1 : index)}
          >
            <button type="button" className="product-media__thumb-button">
              <img
                src={
                  item?.preview_image
                    ? getSizedImageUrl(item?.preview_image?.src, "100x")
                    : item?.src
                      ? getSizedImageUrl(item?.src, "100x")
                      : getSizedImageUrl(
                          item?.previewImage?.originalSrc,
                          "100x",
                        )
                }
                alt={getAltText(item)}
                className={`image product-media__thumb-image`}
                height="80"
                width="80"
                loading="lazy"
              />
            </button>
          </swiper-slide>
        ))}
      </swiper-container>
      <ReactModal open={modalOpen} setOpen={setModalOpen}>
        <ZoomMessage />
        <swiper-container
          {...swiperModalParams}
          // onSwiper={setModalSwiper}
          modules={[EffectFade]}
          className="product-media__modal"
          ref={modalSwiper}
        >
          {variantImage && (
            <swiper-slide
              className="swiper-slide"
              data-product-image
              data-slide={0}
            >
              <ZoomableImage src={getSizedImageUrl(variantImage, "1200x")} />
            </swiper-slide>
          )}
          {filteredMedia.map((item, index) => (
            <swiper-slide
              className="swiper-slide"
              data-product-image
              data-slide={index}
            >
              {isHostedVideo(item) ? (
                <HostedVideo item={item} alt={getAltText(item)} />
              ) : (
                <ZoomableImage
                  src={
                    item?.preview_image
                      ? getSizedImageUrl(item?.preview_image?.src, "1200x")
                      : getSizedImageUrl(item?.src, "1200x")
                  }
                  alt={getAltText(item)}
                />
              )}
            </swiper-slide>
          ))}
        </swiper-container>
        <swiper-container
          {...swiperModalThumbParams}
          modules={[EffectFade]}
          watchSlidesProgress
          className="product-media__modal-thumbs"
          data-product-media-modal-thumbs
        >
          {currentVariant && variantImage && (
            <swiper-slide
              className="swiper-slide"
              // onClick={() => handleThumbClick(0)}
            >
              <img
                src={getSizedImageUrl(variantImage, "80x")}
                height="80"
                width="80"
                loading="lazy"
                className={`image image--product ${getImagePadding(
                  currentVariant.featured_image || currentVariant.image,
                )} ${galleryFade ? "fade" : ""}`}
                alt={getAltText(
                  currentVariant.featured_image || currentVariant.image,
                )}
              />
            </swiper-slide>
          )}
          {filteredMedia.map((item, index) => (
            <swiper-slide
              className="swiper-slide"
              // onClick={() => handleThumbClick(variantImage ? index + 1 : index)}
            >
              <img
                src={
                  item?.preview_image
                    ? getSizedImageUrl(item?.preview_image?.src, "100x")
                    : item?.src
                      ? getSizedImageUrl(item?.src, "100x")
                      : getSizedImageUrl(
                          item?.previewImage?.originalSrc,
                          "100x",
                        )
                }
                alt={getAltText(item)}
                className={`image image--product ${getImagePadding(item)}`}
                height="80"
                width="80"
                loading="lazy"
              />
            </swiper-slide>
          ))}
        </swiper-container>
      </ReactModal>
    </Fragment>
  );
};

export default MediaContent;
